import { useMutation, useQueryClient } from 'react-query'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { ScheduledPostTarget } from 'types'
import { ScheduledPostTargetsClient, UpdateScheduledPostTargetParams } from 'api/clients/ScheduledPostTargetsClient'
import { scheduledPostTargetsKeys } from './scheduledPostTargetsKeys'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'

export interface Data extends ScheduledPostTarget {}

export interface Variables extends UpdateScheduledPostTargetParams {
  scheduled_post_id: number
  target_id: number
  status?: ScheduledPostTarget['status']
}

interface Context {
  previousTarget: ScheduledPostTarget | undefined
  previousTargets: ScheduledPostTarget[] | undefined
}

export const updateScheduledPostTarget = async (variables: Variables, workspaceId: number) => {
  const response = await ScheduledPostTargetsClient.update({
    workspaceId,
    scheduledPostId: variables.scheduled_post_id,
    targetId: variables.target_id,
    params: {
      target_overrides: variables.target_overrides,
      status: variables.status,
    },
  })

  if (response.type !== SUCCESS_RESPONSE_TYPE) {
    throw new Error('Unexpected error response')
  }
  return response.data.scheduled_post_target
}

export const useUpdateScheduledPostTarget = () => {
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()

  return useMutation<Data, unknown, Variables, Context>(
    'updateScheduledPostTarget',
    async (variables) => {
      return updateScheduledPostTarget(variables, workspaceId)
    },
    {
      onMutate: async (updatedTarget) => {
        // Cancel any outgoing refetches
        // (so they don't overwrite our optimistic update)
        await queryClient.cancelQueries(
          scheduledPostTargetsKeys.detail(workspaceId, updatedTarget.scheduled_post_id, updatedTarget.target_id),
        )
        await queryClient.cancelQueries(scheduledPostTargetsKeys.list(workspaceId, updatedTarget.scheduled_post_id))

        // Snapshot the previous values
        const previousTarget = queryClient.getQueryData<ScheduledPostTarget>(
          scheduledPostTargetsKeys.detail(workspaceId, updatedTarget.scheduled_post_id, updatedTarget.target_id),
        )
        const previousTargets = queryClient.getQueryData<ScheduledPostTarget[]>(
          scheduledPostTargetsKeys.list(workspaceId, updatedTarget.scheduled_post_id),
        )

        // Create optimistic target
        const optimisticTarget: ScheduledPostTarget = previousTarget
          ? {
              ...previousTarget,
              target_overrides: updatedTarget.target_overrides || {},
              updated_at: new Date().toISOString(),
            }
          : {
              id: updatedTarget.target_id,
              status: 'pending',
              target_overrides: updatedTarget.target_overrides || {},
              scheduled_post_id: updatedTarget.scheduled_post_id,
              external_integration_id: 0,
              provider: ExternalIntegrationEnum.Instagram,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            }

        // Optimistically update the individual target
        queryClient.setQueryData(
          scheduledPostTargetsKeys.detail(workspaceId, updatedTarget.scheduled_post_id, updatedTarget.target_id),
          optimisticTarget,
        )

        // Optimistically update the target in the list
        if (previousTargets) {
          queryClient.setQueryData(
            scheduledPostTargetsKeys.list(workspaceId, updatedTarget.scheduled_post_id),
            previousTargets.map((target) => (target.id === updatedTarget.target_id ? optimisticTarget : target)),
          )
        }

        // Return a context with the previous values
        return { previousTarget, previousTargets }
      },
      // If the mutation fails, use the context we returned above
      onError: (err, updatedTarget, context) => {
        if (context?.previousTarget !== undefined) {
          queryClient.setQueryData(
            scheduledPostTargetsKeys.detail(workspaceId, updatedTarget.scheduled_post_id, updatedTarget.target_id),
            context.previousTarget,
          )
        }
        if (context?.previousTargets !== undefined) {
          queryClient.setQueryData(
            scheduledPostTargetsKeys.list(workspaceId, updatedTarget.scheduled_post_id),
            context.previousTargets,
          )
        }
      },
      // Always refetch after error or success:
      onSettled: (data, error, variables) => {
        queryClient.invalidateQueries(
          scheduledPostTargetsKeys.detail(workspaceId, variables.scheduled_post_id, variables.target_id),
        )
        queryClient.invalidateQueries(scheduledPostTargetsKeys.list(workspaceId, variables.scheduled_post_id))
      },
    },
  )
}
